import { EventEmitter, Inject, Injectable, Output } from "@angular/core";
import { OKTA_AUTH } from "@okta/okta-angular";
import OktaAuth from "@okta/okta-auth-js";
import { BehaviorSubject } from "rxjs";
import { jwtDecode } from 'jwt-decode';

/**
 * Single source of truth for Okta authentication state and token access.
 * All services access tokens through this service — never inject OktaAuth directly.
 */
@Injectable({
  providedIn: 'root'
})
export class OktaWrapperService {

  /** Cached token and expiry — updated on every renewal, never cleared until failure. */
  private heldToken?: HeldToken;

  /** Decoded JWT claims for the current user. */
  private currentUser?: ICurrentUser;

  /** when Logged out changing this to true to stop auth events. */
  private isLoggingOut = false;
  /**
   * Broadcasts authentication state.
   * Emits true on login or successful renewal.
   * Emits false only on token renewal failure — never on expiry alone.
   */
  public authenticationChanged: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /** Emitted when the user explicitly logs out. */
  @Output() loggedOut: EventEmitter<void> = new EventEmitter();

  /**
   * Set to true to enable detailed token lifecycle logging.
   * Useful for diagnosing unexpected Okta redirects or renewal failures.
   * Should remain false in normal use.
   */
  public diagnosticLogging: boolean = true;

  constructor(@Inject(OKTA_AUTH) private oktaAuth: OktaAuth) {

    // Rehydrate heldToken from storage on startup.
    // Handles browser reopen where the 'added' event never fires
    // but a valid token already exists in localStorage.
    const tokenObj = this.oktaAuth.tokenManager.getTokensSync();
    if (tokenObj?.accessToken) {
      this.updateHeldToken(tokenObj.accessToken);
      this.log(`Token rehydrated from storage — expires at ${this.heldToken?.expiresAt?.toISOString()}`);
    }

    const tokenManager = this.oktaAuth.tokenManager;

    // Fires on initial login — populate held token and signal authenticated.
    tokenManager.on('added', (key: any, token: any) => {
      if (key === 'accessToken') {
        this.updateHeldToken(token);
        this.log(`Token added — expires at ${this.heldToken?.expiresAt?.toISOString()}`);
        this.authenticationChanged.next(true);
        this.log('authenticationChanged emitted: true (added)');
      }
    });

    // Fires when token expires — do NOT clear state here.
    // autoRenew fires immediately after; clearing here creates a gap
    // that causes erratic redirects to the Okta login portal.
    tokenManager.on('expired', (key: any) => {
      if (key === 'accessToken') {
        this.log(`Token expired at ${new Date().toISOString()} — awaiting renewal`);
      }
    });

    // Fires when autoRenew successfully obtains a new token.
    // Update immediately so no request sees an authentication gap.
    tokenManager.on('renewed', (key: any, newToken: any) => {
      if (this.isLoggingOut) {
        return;
      }
      if (key === 'accessToken') {
        this.updateHeldToken(newToken);
        this.log(`Token renewed at ${new Date().toISOString()} — expires at ${this.heldToken?.expiresAt?.toISOString()}`);
        this.authenticationChanged.next(true);
        this.log('authenticationChanged emitted: true (renewed)');
      }
    });

    // Only here do we clear state — renewal genuinely failed, user must re-authenticate.
    tokenManager.on('error', (err: any) => {
      if (this.isLoggingOut) {
        return;
      }
        this.log(`Token renewal error at ${new Date().toISOString()} — clearing state`);
      console.error('OktaWrapperService: token renewal error', err);
      this.heldToken = undefined;
      this.currentUser = undefined;
      this.authenticationChanged.next(false);
      this.log('authenticationChanged emitted: false (error)');
      if (!this.isLoggingOut) {
        this.logout();
      }
    });
  }

  /**
   * Asks Okta directly whether the user is authenticated.
   * Use this as the authoritative check — covers all scenarios including
   * browser reopen, token renewal windows, and first load timing.
   */
  public isAuthenticated(): Promise<boolean> {
    return this.oktaAuth.isAuthenticated();
  }

  /**
   * Synchronous fast-path check using the cached heldToken.
   * Only reliable AFTER the 'added' or 'renewed' event has fired.
   * Use isAuthenticated() when in doubt.
   */
  public isAuthenticatedSync(): boolean {
    if (!this.heldToken) return false;
    return this.heldToken.expiresAt > new Date();
  }

  /** Returns decoded JWT claims for the current user, or undefined if not authenticated. */
  public getUser(): ICurrentUser | undefined {
    return this.currentUser;
  }

  /**
   * Returns the current access token string.
   * Falls back to Okta's token manager if heldToken is not yet populated
   * (e.g. on first load before the 'added' event fires).
   */
  public getAccessToken(): string | undefined {
    return this.heldToken?.token ?? this.oktaAuth.getAccessToken();
  }

  /** Initiates the Okta redirect login flow. */
  public login(): void {
    this.log(`Login initiated at ${new Date().toISOString()}`);
    this.oktaAuth.signInWithRedirect();
  }

  public logout(): void {

    console.log('LOGOUT START', new Date());

    this.isLoggingOut = true;
    this.loggedOut.emit();

    if (!this.oktaAuth.getIdToken()) {
      this.oktaAuth.tokenManager.clear();
      window.location.href = '/login';
      return;
    }

    this.oktaAuth.signOut()
      .then(() => {
        console.log('SIGNOUT COMPLETE', new Date());
      })
      .catch(err => {
        console.error('SIGNOUT ERROR', err);
      });
  }

  /** Updates the cached token and decodes user claims from the JWT. */
  private updateHeldToken(token: any): void {
    this.heldToken = {
      token: token.accessToken,
      expiresAt: new Date(token.expiresAt * 1000)
    };
    this.currentUser = jwtDecode<ICurrentUser>(this.heldToken.token);
  }

  /** Logs a diagnostic message when diagnosticLogging is enabled. */
  private log(message: string): void {
    if (this.diagnosticLogging) {
      console.log(`[OktaWrapperService] ${message}`);
    }
  }
}

interface HeldToken {
  token: string;
  expiresAt: Date;
}

export interface ICurrentUser {
  Groups: string[];
  aud: string;
  auth_time: number;
  cid: string;
  email: string;
  exp: number;
  firstName: string;
  iat: number;
  iss: string;
  jti: string;
  lastName: string;
  scp: string[];
  sub: string;
  uid: string;
  ver: number;
}
